
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { GetStaticPropsContext } from 'next';
import Head from 'next/head';
import { isFilled, asLink, asImageSrc } from '@prismicio/client';
import { SliceZone } from '@prismicio/react';
import styled from '@emotion/styled';
import { components } from '../../slices';
import { createClient } from '../../prismicio';
import BlogLayout from '../../library/atoms/BlogLayout';
import * as prismic from '@prismicio/client';
import dayjs from 'dayjs';
import { PrismicNextLink } from '@prismicio/next';
import Link from 'next/link';
import { H3, Text } from 'library/atoms/Typography';
import Button from 'library/atoms/Button';
import { definedNotNull } from 'shared/presenters/presenter';
import type { Content } from '@prismicio/client';
import { PrismicPreview } from '@prismicio/next';
import { repositoryName } from 'prismicio';
type Params = {
    uid: string;
};
type PageProps = {
    page: Content.BlogDocument;
    nextArticle: Content.BlogDocument | null;
};
export default function Page({ page, nextArticle }: PageProps) {
    return (<>
      <PrismicPreview repositoryName={repositoryName}/>
      <Head>
        <title>
          {isFilled.keyText(page.data.meta_title)
            ? page.data.meta_title
            : page.data.title}
        </title>

        {isFilled.keyText(page.data.meta_description) ? (<meta name="description" content={page.data.meta_description}/>) : (page.data.summary)}

        <meta property="og:type" content="WebSite"/>
        <meta property="og:url" content={`https://eola.co/blog/${page.uid}`}/>
        {isFilled.image(page.data.meta_image) && (<meta property="og:image" content={asImageSrc(page.data.meta_image)}/>)}
      </Head>
      <BlogLayout>
        <Article>
          <FeaturedImage image={page.data.featured_image.url}/>
          <Content>
            <Category>{page.tags[0] || 'TIPS'}</Category>
            <Title>{page.data.title}</Title>
            {isFilled.keyText(page.data.author) && (<Author>by {page.data.author}</Author>)}
            <PublishDate>
              {dayjs(page.data.publish_date || page.first_publication_date).format('D MMMM, YYYY')}
            </PublishDate>
            <SliceZone slices={page.data.slices} components={components}/>
          </Content>

          <CrossSell>
            <H3>Enjoyed this?</H3>
            <Text>
              eola helps hundreds of activity businesses across the world.
            </Text>
            <Link href="/" passHref={true}>
              <Button>Find out more?</Button>
            </Link>
          </CrossSell>

          {nextArticle && (<NextArticle>
              <strong>Next article: </strong>
              <PrismicNextArticleLink href={definedNotNull(asLink(nextArticle))}>
                {nextArticle.data.title}
              </PrismicNextArticleLink>
            </NextArticle>)}
        </Article>
        {isFilled.keyText(page.data.javascript) ? (<span dangerouslySetInnerHTML={{ __html: page.data.javascript }}/>) : null}
      </BlogLayout>
    </>);
}
async function getStaticProps({ params, previewData }: GetStaticPropsContext<Params>) {
    // The `previewData` parameter allows your app to preview
    // drafts from the Page Builder.
    const client = createClient({ previewData });
    const pages = await client.getAllByType('blog', {
        orderings: {
            field: 'my.blog.publish_date',
            direction: 'desc'
        }
    });
    const currentIndex = pages.findIndex(page => page.uid === params!.uid);
    const nextArticle = currentIndex > 0 ? pages[currentIndex - 1] : null;
    try {
        const page = await client.getFirst({
            filters: [
                prismic.filter.at('my.blog.uid', params!.uid),
                prismic.filter.at('document.type', 'blog')
            ]
        });
        return {
            props: { page, nextArticle: nextArticle },
            revalidate: 60,
            notFound: !page
        };
    }
    catch (e: any) {
        if (e.message === 'No documents were returned') {
            return {
                revalidate: 60,
                notFound: true
            };
        }
        else {
            throw e;
        }
    }
}
export const getStaticPaths = async () => {
    return {
        paths: [],
        fallback: 'blocking'
    };
};
const CrossSell = styled.div(({ theme }) => ({
    fontSize: '.9em',
    padding: '.8125em 1.25em',
    border: '1px solid #e4e4e4',
    borderRadius: '2px',
    background: '#fdfdfd',
    marginTop: theme.space[4],
    [theme.mediaQueries.xl]: {
        width: '70%'
    }
}));
const NextArticle = styled.div(({ theme }) => ({
    marginTop: theme.space[3],
    fontSize: '16px',
    fontWeight: 400,
    color: 'rgb(72, 72, 72)'
}));
const PrismicNextArticleLink = styled(PrismicNextLink)(() => ({
    color: 'rgb(102, 102, 102) !important',
    textDecoration: 'underline'
}));
const Article = styled.article(({ theme }) => ({
    padding: theme.mixins.pxSpread([theme.space[6], theme.space[5]]),
    ...theme.mixins.centeredContainer('xl'),
    position: 'relative',
    p: {
        display: 'block',
        marginBlockStart: '1em',
        marginBlockEnd: '1em',
        marginInlineStart: '0px',
        marginInlineEnd: '0px'
    },
    a: {
        color: '#ec165a',
        ':hover': {
            textDecoration: 'underline'
        }
    }
}));
const FeaturedImage = styled.div<{
    image?: string | null;
}>(({ theme, image }) => ({
    background: '#3e4ea0 center 60% no-repeat',
    backgroundSize: 'cover',
    backgroundImage: `url(${image})`,
    borderRadius: theme.radii[2],
    boxShadow: '0 8px 24px rgba(141,151,158,0.2)',
    width: '100%',
    minHeight: '300px',
    [theme.mediaQueries.xl]: {
        minHeight: '34.375em',
        width: '420px',
        position: 'absolute',
        right: 0
    }
}));
const Content = styled.div(({ theme }) => ({
    width: '100%',
    margin: 'auto',
    maxWidth: '820px',
    padding: `${theme.space[1]}px ${theme.space[1]}px`,
    marginTop: theme.space[4],
    [theme.mediaQueries.xl]: {
        width: '85%',
        margin: 'unset',
        padding: `${theme.space[4]}px ${theme.space[6]}px`,
        backgroundColor: theme.colors.white,
        borderRadius: theme.radii[4],
        marginTop: '150px',
        ':before': {
            content: '""',
            display: 'block',
            float: 'right',
            height: '25em',
            width: '13.875em'
        }
    }
}));
const Category = styled.div(() => ({
    fontSize: '16px',
    fontWeight: 400,
    color: 'rgb(72, 72, 72)'
}));
const Title = styled.h1(() => ({
    fontSize: '48px',
    fontWeight: 700,
    color: 'rgb(72, 72, 72)'
}));
const Author = styled.div(({ theme }) => ({
    fontSize: '16px',
    fontWeight: 400,
    color: 'rgb(72, 72, 72)',
    marginBottom: theme.space[1]
}));
const PublishDate = styled.div(() => ({
    fontSize: '16px',
    fontWeight: 400,
    color: 'rgb(72, 72, 72)'
}));

    async function __Next_Translate__getStaticProps__1966c8dead3__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/blog/[uid]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1966c8dead3__ as getStaticProps }
  